import { Box, Typography, Button, Stack } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import SoopraSmile from "../../svg/SoopraSmile";
import { useNavigate } from 'react-router-dom';
import useDeviceType from '../../component/useDeviceType';
import ChatIcon2 from "../../svg/ChatIcon2";
import ProfileIcon2 from "../../svg/ProfileIcon2";
import { useTheme } from '@mui/material/styles';

interface FanCreatorProfileItemType {
  profileData: {
    fullName: string;
    profilePicUrl: string;
    userName: string;
    shortBio: string;
    fanCount: number;
    messageCount: number;
    voiceId: string;
  };
}

export default function FanCreatorProfileItem(
  props: FanCreatorProfileItemType
) {
  const {
    fullName,
    profilePicUrl,
    userName,
    shortBio,
  } = props.profileData;

  // Setup the chat URL
  const chatUrl = `/${userName}/chat`;
  const profileUrl = `/${userName}`;
  const navigate = useNavigate();
  const { isTouchDevice, isMouseDevice } = useDeviceType();
  const theme = useTheme();

  return (
    <Stack
      onClick={!isMouseDevice ? (e) => navigate(profileUrl) : undefined}
      sx={{
        px: { xs: 1.25, md: 1.5 },
        py: { xs: 1, sm: 1.25, md: 2 },
        width: '100%',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'inline-block',
          width: '100%',
          height: 'auto',
          ...(isMouseDevice && {
            '&:hover .overlay': {
              zIndex: 1,
              opacity: 1,
            },
          }),
        }}
      >

        {
          !profilePicUrl ?
            <Box
              width='100%'
              height='auto'
              sx={{
                backgroundColor: (theme) => theme.palette.background['paper-elevation-11'],
                aspectRatio: '1',
                border: '2px solid',
                borderColor: (theme) => theme.palette.background['paper-elevation-11'],
                borderRadius: '35%',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  p: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <SoopraSmile
                  width='100%'
                  height='auto'
                  color={theme.palette.primary.main}
                />
              </Box>
            </Box> :
            <Avatar
              src={profilePicUrl}
              alt={fullName}
              imgProps={{ loading: 'lazy', decoding: 'async' }}
              sx={{
                width: '100%',
                height: 'auto',
                objectFit: !!profilePicUrl ? 'cover' : 'contain',
                aspectRatio: '1',
                border: '2px solid',
                borderColor: (theme) => theme.palette.background['paper-elevation-11'],
                borderRadius: '35%',
                '& img': {
                  objectFit: 'inherit',
                  p: !!profilePicUrl ? 0 : 2,
                }
              }}
            />
        }
        <Box
          className='overlay'
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backdropFilter: 'blur(2px)',
            borderRadius: (theme) => theme.shape.borderRadius,
            opacity: 0,
            zIndex: -1,
            transition: 'opacity 0.4s',
            backgroundColor: (theme) => theme.palette.action.imagehover,
            pointerEvents: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
          }}
        >
          <Button
            variant='contained'
            color='primary'
            size='medium'
            id={`button-voice-${userName}`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(chatUrl, { state: { useVoiceChat: false } });
            }}
            sx={{ zIndex: 2 }}
            startIcon={<ChatIcon2 fill={theme.palette.primary.contrastText} />}
            href={chatUrl}
          >
            Chat
          </Button>
          <Button
            variant='contained'
            color='secondary'
            size='medium'
            id={`button-profile-${userName}`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(profileUrl);
            }}
            sx={{ zIndex: 2 }}
            startIcon={<ProfileIcon2 fill={theme.palette.secondary.contrastText} />}
            href={profileUrl}
          >
            View Profile
          </Button>
        </Box>
      </Box>
      <Stack
        sx={{
          px: 1,
          pt: 1.5,
        }}
      >
        <Typography variant='h7' color='text.primary' align='left'>
          {fullName}
        </Typography>
        <Typography
          component='p'
          variant='body2'
          color='text.secondary'
          align='left'
        >
          {`@${userName}`}
        </Typography>
        <Typography
          component='p'
          variant='body2'
          color='text.primary'
          align='left'
          sx={{
            display: '-webkit-box',
            overflow: 'hidden',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            // height: '2.8em',
            // wordBreak: 'break-all',
          }}
        >
          {shortBio}
        </Typography>
        {/* <Typography
            component='p'
            variant='body2'
            color='text.secondary'
            align='left'
          >
            Technology/Entrepreneurship
          </Typography> */}
      </Stack>
    </Stack>
  );
}
